// React dependencies
import React, { useEffect, useState } from 'react';

// External dependencies
import { FormControl, FormControlLabel, RadioGroup } from '@material-ui/core';
import PropTypes from 'prop-types';

// Internal dependencies
import { IsEmpty } from '../../../../../common/checks';
import MText from '../../../../../components/Common/MText';
import Colours from '../../../../../styles/colours';
import MTextField from '../../../../../components/MTextField';
import MRadio from '../../../../../components/Common/MRadio';
import PoiTags from '../../PoiTags';

const HourOption = {
  AllDay:   '24 Hours',
  Custom:   `Others`,
  NotAvail: 'Not Available',
};

const TagsOthers = (props) => {
  const [hourOptionSelected, setHourOptionSelected] = useState(null);

  const hours = String(props.data?.hours || '');

  useEffect(() => {
    if (!IsEmpty(hours)) {
      const strHours = String(hours).toLowerCase();
      if (strHours === HourOption.AllDay.toLowerCase()) {
        setHourOptionSelected(HourOption.AllDay);
      } else if (strHours === HourOption.NotAvail.toLowerCase()) {
        setHourOptionSelected(HourOption.NotAvail);
      } else {
        setHourOptionSelected(HourOption.Custom);
      }
    }
  }, [hours]);

  const onDescriptionChanged = (newValue) => {
    const hasThreeConsecutiveEnters = /\n{3,}/.test(newValue);

    if (!hasThreeConsecutiveEnters) {
      props.onUpdatePoiData('description', newValue);
    }
  };

  const onCustomHourChanged = (newValue) => {
    props.onUpdatePoiData('hours', newValue);
  };

  return (
    <div className="d-flex flex-column px-1">
      <div className="w-100 d-flex flex-column">
        <div className="d-flex flex-column align-items-center">
          <MText size={13} text="Description of place" />
          <MText size={11} colour={Colours.LightGray4} text="(max. 1000 characters)" />
        </div>

        <MTextField
          className="mt-2"
          minRows={8}
          maxRows={8}
          value={props.data.description}
          placeholder={props.disabled ? '' : 'Share what is interesting about this place'}
          onChange={onDescriptionChanged}
          disabled={props.disabled}
          maxCharacters={1000}
          showCharactersCount
        />
      </div>

      <div className="w-100 d-flex flex-column">
        <div className="d-flex flex-row justify-content-center align-items-center">
          <MText text="Tags" size={13} />
          <MText text="*" colour={Colours.Red} style={{ marginLeft: '0.2rem' }} />
        </div>
        <div className="d-flex flex-column align-items-center">
          <MText text="(tap 'Enter' after each tag)" colour={Colours.LightGray4} size={11} />
        </div>

        {/**
       * Due to how the internal code of PoiTags is done, we can't apply
       * className to PoiTags to set the margin using className; therefore
       * we wrap PoiTags with a div to apply the margin on the div.
       */}
        <div className="d-flex flex-column mt-2">
          <PoiTags
            userTags={props.data.tags}
            onSelectTag={(tag) => props.onUpdatePoiData('tags', [...props.data.tags, tag])}
            onUserTagsChange={(tags) => props.onUpdatePoiData('tags', tags)}
            textColour={Colours.Black}
            disabled={props.disabled}
          />
        </div>
      </div>

      <div className="mt-4 w-100 d-flex flex-column">
        <div className="d-flex flex-column align-items-center">
          <MText text="Opening hours" size={13} />
        </div>

        <FormControl>
          <RadioGroup
            aria-labelledby="hours-radio-group"
            name="hours-radio-group"
            value={hourOptionSelected}
            row={false}
            onChange={(e) => {
              const option = e.target.value;
              props.onUpdatePoiData('hours',
                option === HourOption.AllDay
                  ? HourOption.AllDay
                  : option === HourOption.NotAvail
                    ? HourOption.NotAvail
                    : '');
              setHourOptionSelected(option);
            }}
          >
            <FormControlLabel
              className="my-auto mx-0"
              key={HourOption.AllDay}
              value={HourOption.AllDay}
              disabled={props.disabled}
              control={<MRadio />}
              label={(
                <MText
                  style={{ minWidth: '5rem' }}
                  text={HourOption.AllDay}
                  size={12}
                  className="d-block"
                />
              )}
            />
            <FormControlLabel
              className="my-auto mx-0"
              key={HourOption.NotAvail}
              value={HourOption.NotAvail}
              disabled={props.disabled}
              control={<MRadio />}
              label={(
                <MText
                  style={{ minWidth: '5rem' }}
                  text={HourOption.NotAvail}
                  size={12}
                  className="d-block"
                />
              )}
            />
            <FormControlLabel
              className="my-auto mx-0"
              key={HourOption.Custom}
              value={HourOption.Custom}
              disabled={props.disabled}
              control={<MRadio />}
              label={(
                <MText
                  style={{ minWidth: '5rem' }}
                  text={HourOption.Custom}
                  size={12}
                  className="d-block"
                />
              )}
            />
          </RadioGroup>
        </FormControl>

        {
          hourOptionSelected === HourOption.Custom && (
            <MTextField
              className="ml-3 mt-2"
              value={hours || ''}
              placeholder="Please fill in"
              onChange={onCustomHourChanged}
              minRows={4}
              maxRows={4}
              disabled={props.disabled}
            />
          )
        }

        {
          !props.disabled && <MText className="mt-4" text="*required" colour={Colours.Red} />
        }
      </div>
    </div>
  );
};

TagsOthers.propTypes = {
  data: PropTypes.shape({
    tags:        PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
    hours:       PropTypes.string,
  }),
  disabled:        PropTypes.bool,
  onUpdatePoiData: PropTypes.func.isRequired,
};

TagsOthers.defaultProps = {
  data:     {},
  disabled: false,
};
export default TagsOthers;
